/**
 * API Client for PCI Images Worker
 * 
 * Uses Monday.com session token for authentication.
 * Includes retry logic with exponential backoff for transient failures.
 */

import mondaySdk from 'monday-sdk-js';
import type { ListingImage, UploadUrlResponse, ListImagesResponse } from '../types';

const monday = mondaySdk();
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

// HTTP status codes that should trigger a retry
const RETRYABLE_STATUS_CODES = [429, 500, 502, 503, 504];

/**
 * Map HTTP status codes to user-friendly error messages
 */
function getErrorMessageForStatus(status: number, context: string): string {
  switch (status) {
    case 400:
      return `Invalid request: ${context}`;
    case 401:
      return 'Authentication failed. Please refresh the page.';
    case 403:
      return 'Access denied. You may not have permission for this action.';
    case 404:
      return 'Resource not found.';
    case 413:
      return 'File too large. Maximum size is 10MB.';
    case 429:
      return 'Too many requests. Please wait a moment.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Server error. Please try again.';
    default:
      return `Request failed (${status})`;
  }
}

/**
 * Get auth headers with Monday session token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const tokenResponse = await monday.get('sessionToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${tokenResponse.data}`,
  };
}

/**
 * Parse error response to extract server error message
 */
async function parseErrorResponse(res: Response, defaultContext: string): Promise<string> {
  // First, try to get a meaningful message from the response body
  try {
    const contentType = res.headers.get('content-type') || '';
    
    if (contentType.includes('application/json')) {
      const body = await res.json();
      if (body.error && typeof body.error === 'string') {
        return body.error;
      }
      if (body.message && typeof body.message === 'string') {
        return body.message;
      }
      // Cloudflare Images specific error format
      if (body.errors && Array.isArray(body.errors) && body.errors.length > 0) {
        return body.errors.map((e: { message?: string }) => e.message || 'Unknown error').join(', ');
      }
    } else {
      // Try to read as text for non-JSON responses
      const text = await res.text();
      if (text && text.length < 200) {
        return text;
      }
    }
  } catch {
    // Response body parsing failed
  }
  
  // Fall back to status-based message
  return getErrorMessageForStatus(res.status, defaultContext);
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with retry logic for transient failures
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  context: string,
  maxRetries: number = MAX_RETRIES
): Promise<Response> {
  let lastError: Error | null = null;
  let lastResponse: Response | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(url, options);
      
      // If successful or non-retryable error, return immediately
      if (res.ok || !RETRYABLE_STATUS_CODES.includes(res.status)) {
        return res;
      }
      
      // Store for potential final error
      lastResponse = res;
      
      // If we have retries left, wait and try again
      if (attempt < maxRetries) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        console.log(`Retry ${attempt + 1}/${maxRetries} for ${context} after ${delayMs}ms (status: ${res.status})`);
        await sleep(delayMs);
      }
    } catch (err) {
      // Network error - may be transient
      lastError = err instanceof Error ? err : new Error(String(err));
      
      if (attempt < maxRetries) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        console.log(`Retry ${attempt + 1}/${maxRetries} for ${context} after ${delayMs}ms (network error)`);
        await sleep(delayMs);
      }
    }
  }
  
  // All retries exhausted
  if (lastResponse) {
    return lastResponse;
  }
  
  throw lastError || new Error(`Failed to ${context} after ${maxRetries} retries`);
}

/**
 * Get all images for a listing
 */
export async function getImages(listingId: string): Promise<ListImagesResponse> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/${listingId}`,
    { headers },
    'fetch images'
  );
  
  if (!res.ok) {
    const message = await parseErrorResponse(res, 'fetch images');
    throw new Error(message);
  }
  return res.json();
}

/**
 * Get a direct upload URL from Cloudflare
 * 
 * Server assigns position atomically to avoid race conditions.
 */
export async function getUploadUrl(
  listingId: string,
  filename: string
): Promise<UploadUrlResponse> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/upload-url`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ listing_id: listingId, filename }),
    },
    'get upload URL'
  );
  
  if (!res.ok) {
    const message = await parseErrorResponse(res, 'get upload URL');
    throw new Error(message);
  }
  return res.json();
}

/**
 * Upload a file directly to Cloudflare Images
 * Note: This goes directly to Cloudflare, not through our API
 * 
 * IMPORTANT: Cannot use fetchWithRetry here because FormData body streams
 * are consumed on first read. Must create fresh FormData for each attempt.
 * 
 * Uses 1 retry only - if Cloudflare rejects the file, retrying usually won't help.
 */
export async function uploadToCloudflare(
  uploadUrl: string,
  file: File
): Promise<void> {
  const maxRetries = 1; // Only retry once for uploads
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create fresh FormData for each attempt (body stream is consumed on read)
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      
      if (res.ok) {
        return; // Success!
      }
      
      // Parse error for better message
      const message = await parseErrorResponse(res, `upload ${file.name}`);
      
      // Only retry on transient errors (rate limiting, server errors)
      if (!RETRYABLE_STATUS_CODES.includes(res.status)) {
        throw new Error(message);
      }
      
      // If we have retries left, wait and try again
      if (attempt < maxRetries) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        console.log(`Retry upload ${file.name} after ${delayMs}ms (status: ${res.status})`);
        await sleep(delayMs);
      } else {
        throw new Error(message);
      }
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      
      // If it's not a network error (already has a message), don't retry
      if (lastError.message && !lastError.message.includes('fetch')) {
        throw lastError;
      }
      
      // Network error - retry if we have attempts left
      if (attempt < maxRetries) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        console.log(`Retry upload ${file.name} after ${delayMs}ms (network error)`);
        await sleep(delayMs);
      }
    }
  }
  
  throw lastError || new Error(`Failed to upload ${file.name}`);
}

/**
 * Confirm an upload completed
 */
export async function confirmUpload(imageId: string): Promise<ListingImage> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/confirm`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ image_id: imageId }),
    },
    'confirm upload'
  );
  
  if (!res.ok) {
    const message = await parseErrorResponse(res, 'confirm upload');
    throw new Error(message);
  }
  return res.json();
}

/**
 * Reorder images
 */
export async function reorderImages(
  listingId: string,
  order: string[]
): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/reorder`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ listing_id: listingId, order }),
    },
    'reorder images'
  );
  
  if (!res.ok) {
    const message = await parseErrorResponse(res, 'reorder images');
    throw new Error(message);
  }
}

/**
 * Toggle hero image for a listing
 */
export async function setHero(
  listingId: string,
  imageId: string
): Promise<ListImagesResponse> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/set-hero`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ listing_id: listingId, image_id: imageId }),
    },
    'set hero image'
  );

  if (!res.ok) {
    const message = await parseErrorResponse(res, 'set hero image');
    throw new Error(message);
  }
  return res.json();
}

/**
 * Delete an image
 * 
 * Note: Image IDs contain slashes (e.g., "listing-123/uuid"), so we must URL-encode them
 */
export async function deleteImage(imageId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetchWithRetry(
    `${API_BASE}/api/images/${encodeURIComponent(imageId)}`,
    {
      method: 'DELETE',
      headers,
    },
    'delete image'
  );
  
  if (!res.ok) {
    const message = await parseErrorResponse(res, 'delete image');
    throw new Error(message);
  }
}
